<template>
  <VModal
    title="Preview Campaign"
    v-if="opened"
    :actions="actions"
    classType="primary"
    version="new"
  >
    <WithLoader :isLoading="isLoading" classType="primary">
      <div class="campaign-preview__wrapper" v-if="preview">
        <div class="campaign-preview__details">
          <div class="name">
            <span class="title">Name:</span>
            <div class="value">{{ campaign.name }}</div>
          </div>
          <div class="contact-numbers">
            <span class="title">Numbers of contacts:</span>
            <div class="value">{{ preview.count.toLocaleString("en-US") }}</div>
          </div>
        </div>

        <div class="campaign-preview__thumbnails">
          <span class="title">Preview:</span>
          <VThumbnailsPreview
            :thumbnails="thumbnails"
            :type="campaign.type"
            maxItemsCount="2"
          ></VThumbnailsPreview>
        </div>

        <div
          v-if="campaign.type === 'postcard'"
          class="campaign-preview__price"
        >
          <div class="left-block">
            <span>You Will Spend:</span>
            <div class="estimated-price">
              ${{
                (preview.price / 100).toLocaleString("en-US", {
                  maximumFractionDigits: 2
                })
              }}
            </div>
          </div>
          <div class="right-block">
            <VHint :text="hintText" size="big" preLine></VHint>
          </div>
        </div>
      </div>
    </WithLoader>
  </VModal>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import withNavigate from "@/mixins/withNavigate";
import withToasted from "@/mixins/withToasted";
import VModal from "@/components/VModal";
import VThumbnailsPreview from "@/components/VThumbnailsPreview";
import VHint from "@/components/VHint";
import { mapGetters, mapActions } from "vuex";

export default Vue.component("CampaignsPreviewModal", {
  props: {
    opened: VueTypes.bool.def(false),
    campaignId: VueTypes.string,
    templateId: VueTypes.string,
    thumbnails: VueTypes.array
  },

  components: {
    VModal,
    VThumbnailsPreview,
    VHint
  },

  data() {
    return {
      frontPreview: null,
      backPreview: null,
      template: null,
      hintText:
        "This price calculated on your subscription plan basis and amount of contacts in this campaign.\n" +
        "\n" +
        "The final price might changed if any of contacts will be rejected or not sent for any reason."
    };
  },

  mixins: [withNavigate, withToasted],

  computed: {
    actions() {
      return [
        {
          label: "Cancel",
          value: "cancel",
          inverted: true,
          type: "primary",
          size: "wide",
          noBorder: true,
          click: () => this.$emit("close")
        },
        {
          label: "Send!",
          type: "primary",
          value: "send",
          size: "regular",
          disabled: !this.canSendCampaign || this.isLoading,
          click: () => this.send()
        }
      ];
    },

    campaign() {
      return this.getCampaign(this.campaignId);
    },

    preview() {
      return this.getPreview(this.campaignId);
    },

    isLoading() {
      return (
        this.$store.state.campaigns.isLoading ||
        this.$store.state.templates.isLoading
      );
    },

    ...mapGetters("campaigns", [
      "getCampaign",
      "getPreview",
      "canSendCampaign"
    ]),
    ...mapGetters("templates", ["getTemplate"]),
    ...mapGetters("account", ["getAccount"])
  },

  methods: {
    send() {
      this.sendCampaign({
        id: this.campaignId,
        onSuccess: () => {
          this.fetchCampaign({
            id: this.campaignId,
            onSuccess: () => {
              this.$emit("close");
              this.toast(
                `Campaign ${this.campaign.name} has been sent successfully!`
              );
            }
          });
        }
      });
    },

    ...mapActions("campaigns", [
      "fetchPreview",
      "sendCampaign",
      "fetchCampaign"
    ]),

    ...mapActions("templates", ["fetchStaticPreview", "fetchTemplateById"])
  },

  async mounted() {
    if (!this.campaignId) {
      this.navigate("/campaigns");
    }

    this.fetchPreview(this.campaignId, {
      onSuccess: () => {},
      onError: e => this.toast(e, "error")
    });

    // COMMENTED FOR A WHILE FOR THIS RELEASE
    // await this.fetchTemplateById({
    //   id: this.templateId,
    //   onSuccess: t => {
    //     this.template = t;
    //   }
    // });
    //
    // this.backPreview = await fetchStaticTemplate(
    //   this.template,
    //   "back",
    //   this.getAccount.id,
    //   "api"
    // );
    //
    // this.frontPreview = await fetchStaticTemplate(
    //   this.template,
    //   "front",
    //   this.getAccount.id,
    //   "api"
    // );
  }
});
</script>

<style lang="scss" scoped>
$ns: "campaign-preview";

.#{$ns}__wrapper {
  color: $main-text-color;
  font-size: 16px;
  line-height: 1.2;
}

.title {
  display: inline-block;
  margin-bottom: 15px;
}

.#{$ns}__details {
  display: flex;
  flex: 1;

  & > div {
    width: 50%;
  }

  > div:first-child {
    margin-right: 30px;
  }

  .name,
  .contact-numbers {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    .value {
      font-size: 22px;
      font-family: Lato-Bold, Arial, sans-serif;
      width: 90%;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      text-align: start;
    }
  }
}

.#{$ns}__thumbnails {
  margin-top: 30px;
  display: flex;
  flex-direction: column;

  & > *:first-child {
    align-self: flex-start;
  }

  &/deep/.v-thumbnails-preview__wrapper {
    max-width: 390px;
    max-height: 222px;
    .v-preview-image {
      .image-wrapper {
        max-width: 100%;
        height: 100%;
      }
    }
  }
}

.#{$ns}__price {
  margin-top: 30px;
  display: flex;
  justify-content: flex-end;

  .left-block {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-right: 10px;

    span {
      margin-bottom: 10px;
    }
  }

  .estimated-price {
    color: $primary-color;
    font-size: 22px;
    font-family: Lato-Bold, Arial, sans-serif;
  }
}
</style>
